import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { logoutUser } from "../../api/services/authService";
import { getLoginPageByRole } from "../../utils/authHelpers";
import { useTranslation } from "react-i18next";
import LanguageToggle from "../LanguageToggle";
import {
  LayoutDashboard,
  UserPlus,
  Users,
  Server,
  Box,
  Share2,
  LogOut,
  X,
  Building2,
  Swords,
  Radar,
} from "lucide-react";

export const ADMIN_NAV_LINKS = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/home" },
  {
    label: "Register Organizations",
    icon: Building2,
    path: "/admin/register-organizations",
  },
  { label: "Register Admins", icon: UserPlus, path: "/admin/register-admins" },
  { label: "User Management", icon: Users, path: "/admin/users" },
  { label: "Boxes", icon: Box, path: "/boxes" },
  { label: "Topology", icon: Share2, path: "/topology" },
  {
    label: "Team Challenges",
    icon: Swords,
    path: "/admin/team-challenges",
  },
  {
    label: "Command and Control",
    icon: Radar,
    path: "/admin/command-and-control",
  },
];

export default function PlatformAdminSidebar({ isCollapsed = false, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { i18n } = useTranslation();
  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "ar" : "en";
    i18n.changeLanguage(newLang);
    localStorage.setItem("language", newLang);
    document.documentElement.dir = newLang === "ar" ? "rtl" : "ltr";
  };
  // isCollapsed has two readings depending on viewport:
  //  - mobile (<lg): true = drawer hidden off-screen, false = drawer slid in
  //  - desktop (lg+): true = icon-only rail (w-16), false = full sidebar (w-60)
  return (
    <>
      {/* Mobile backdrop — only when the drawer is showing on mobile */}
      {!isCollapsed && (
        <div
          className="fixed inset-0 z-20 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        dir="ltr"
        className={`
  fixed top-16 sm:top-19 left-0 bottom-0 w-60 flex flex-col z-30
  qv-glass-sidebar
  max-lg:bg-(--color-bg)! max-lg:backdrop-blur-none!
  transition-[transform,width] duration-300 ease-in-out
        ${isCollapsed ? "-translate-x-full" : "translate-x-0"}
        lg:translate-x-0
        ${isCollapsed ? "lg:w-16" : "lg:w-60"}
      `}
      >
        {/* Nav links */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto overflow-x-hidden">
          <div
            className={`text-[9px] font-bold tracking-[0.2em] uppercase text-(--color-text-muted) px-3 mb-2 select-none ${isCollapsed ? "lg:hidden" : ""}`}
          >
            Navigation Menu
          </div>{" "}
          {ADMIN_NAV_LINKS.map(({ label, icon: Icon, path, disabled }) => {
            const isActive =
              path === "/home"
                ? location.pathname === "/home"
                : location.pathname.startsWith(path);

            const isDisabled = Boolean(disabled);

            return (
              <Link
                key={label}
                to={path}
                title={isCollapsed ? label : undefined}
                onClick={(event) => {
                  if (isDisabled) {
                    event.preventDefault();
                  }
                }}
                style={
                  !isDisabled && isActive
                    ? {
                        background:
                          "linear-gradient(180deg, var(--color-primary-100), var(--color-primary-50))",
                        boxShadow:
                          "0 0 0 1px color-mix(in srgb, var(--color-primary) 30%, transparent), 0 10px 24px -12px var(--color-shadow-primary-hover)",
                      }
                    : undefined
                }
                className={`
                flex items-center gap-3 px-3 py-2.5 rounded-sm text-[13px] font-medium
                transition-all duration-150 group whitespace-nowrap border
                ${isCollapsed ? "lg:justify-center lg:gap-0 lg:px-2" : ""}
                ${
                  isDisabled
                    ? "text-(--color-text-muted) opacity-60 cursor-not-allowed border-transparent"
                    : isActive
                      ? "text-(--color-text-primary) border-transparent"
                      : "text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-white/3 dark:hover:bg-white/3 border-transparent"
                }
              `}
              >
                <Icon
                  size={17}
                  className={
                    isDisabled
                      ? "text-(--color-text-muted) shrink-0"
                      : isActive
                        ? "text-(--color-primary) shrink-0"
                        : "text-(--color-text-muted) group-hover:text-(--color-primary) shrink-0"
                  }
                />
                <span className={isCollapsed ? "lg:hidden" : ""}>{label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Bottom actions */}
        <div className="px-3 py-4 space-y-0.5 border-t border-(--color-border)">
          <div
            className={`sm:hidden flex items-center justify-between px-3 py-2 ${isCollapsed ? "lg:hidden" : ""}`}
          >
            <span className="text-xs text-(--color-text-muted) font-medium">
              Language
            </span>
            <LanguageToggle
              lang={i18n.language}
              toggleLanguage={toggleLanguage}
              standalone={false}
            />
          </div>

          <button
            title={isCollapsed ? "Logout" : undefined}
            onClick={async () => {
              setIsLoggingOut(true);
              try {
                await Promise.all([
                  logoutUser(),
                  new Promise((resolve) => setTimeout(resolve, 1000)),
                ]);
                const loginPage = getLoginPageByRole(user?.roles);
                logout();
                navigate(loginPage, { replace: true });
              } catch {
                const loginPage = getLoginPageByRole(user?.roles);
                logout();
                navigate(loginPage, { replace: true });
              } finally {
                setIsLoggingOut(false);
              }
            }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors ${isCollapsed ? "lg:justify-center lg:gap-0 lg:px-2" : ""}`}
          >
            <LogOut size={17} className="shrink-0" />
            <span className={isCollapsed ? "lg:hidden" : ""}>Logout</span>
          </button>
        </div>
      </aside>

      {/* Logout full-screen overlay */}
      {isLoggingOut && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-9999">
          <div className="flex flex-col items-center gap-4">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 border-4 border-(--color-primary)/20 rounded-full" />
              <div className="absolute inset-0 border-4 border-transparent border-t-(--color-primary) rounded-full animate-spin" />
            </div>
            <p className="text-white font-semibold">Logging out…</p>
          </div>
        </div>
      )}
    </>
  );
}
